/**
 * MediaRecorder wrapper for kid voice capture.
 *
 * Opens the microphone on first `start()`, reuses the stream for
 * subsequent recordings, and auto-stops after a configurable maximum
 * duration (5 seconds by default) so a stuck finger doesn't run forever.
 *
 * Output: a single `Blob` with MIME type `audio/webm;codecs=opus` on
 * Chrome / Android, or `audio/mp4` on iOS Safari 14.3+. Both are accepted
 * by Whisper on Workers AI.
 *
 * Runtime gotchas:
 *   - getUserMedia requires a user gesture + HTTPS on iOS Safari.
 *   - navigator.mediaDevices is undefined on insecure contexts.
 *   - Older iOS (<14.3) lacks MediaRecorder entirely.
 *   - MediaRecorder.isTypeSupported is optional — guard reads.
 *
 * Every entry point throws a RecorderError with a `.reason` enum so the
 * UI can show a specific bilingual message instead of a generic "failed".
 */

export type RecorderReason =
  | 'unsupported'
  | 'permission_denied'
  | 'no_mic_found'
  | 'not_running'
  | 'encoding_failed'
  | 'unknown';

export class RecorderError extends Error {
  constructor(public reason: RecorderReason, message: string) {
    super(message);
    this.name = 'RecorderError';
  }
}

export interface RecordResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

export class Recorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private startedAt = 0;
  private resolve: ((r: RecordResult) => void) | null = null;
  private reject: ((err: Error) => void) | null = null;
  private timeout: ReturnType<typeof setTimeout> | null = null;

  /** Maximum recording length (ms). Defaults to 5000. */
  readonly maxMs: number;

  constructor(maxMs = 5000) {
    this.maxMs = maxMs;
  }

  /** True if this browser can record audio at all. */
  static isSupported(): boolean {
    if (typeof navigator === 'undefined') return false;
    if (!navigator.mediaDevices) return false;
    if (typeof navigator.mediaDevices.getUserMedia !== 'function') return false;
    if (typeof MediaRecorder === 'undefined') return false;
    return true;
  }

  /** Start a recording. Must be called from a user gesture on iOS Safari. */
  async start(): Promise<void> {
    if (this.recorder?.state === 'recording') return;

    if (!Recorder.isSupported()) {
      throw new RecorderError(
        'unsupported',
        'This browser cannot record audio',
      );
    }

    if (!this.stream) {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      } catch (err) {
        const name = err instanceof Error ? err.name : '';
        if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
          throw new RecorderError('permission_denied', 'Microphone access was denied');
        }
        if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
          throw new RecorderError('no_mic_found', 'No microphone found');
        }
        throw new RecorderError('unknown', String(err));
      }
    }

    const mime = pickMimeType();
    try {
      this.recorder = new MediaRecorder(
        this.stream,
        mime ? { mimeType: mime } : undefined,
      );
    } catch (err) {
      throw new RecorderError('encoding_failed', String(err));
    }

    this.chunks = [];
    this.startedAt = performance.now();

    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.onstop = () => {
      const mimeType = this.recorder?.mimeType || 'audio/webm';
      const blob = new Blob(this.chunks, { type: mimeType });
      const durationMs = performance.now() - this.startedAt;
      if (this.resolve) this.resolve({ blob, mimeType, durationMs });
      this.resolve = null;
      this.reject = null;
    };
    this.recorder.onerror = (e) => {
      if (this.reject) {
        this.reject(new RecorderError('encoding_failed', String(e)));
      }
      this.resolve = null;
      this.reject = null;
    };

    this.recorder.start();

    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.stop().catch(() => undefined), this.maxMs);
  }

  /** Stop the current recording. Returns the captured audio once encoded. */
  stop(): Promise<RecordResult> {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (!this.recorder || this.recorder.state !== 'recording') {
      return Promise.reject(new RecorderError('not_running', 'Recorder is not running'));
    }
    return new Promise<RecordResult>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      this.recorder!.stop();
    });
  }

  /** Release the microphone. Call when leaving the stage. */
  release(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.recorder = null;
  }
}

function pickMimeType(): string | undefined {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  const isSupported =
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  if (!isSupported) return undefined;
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* some UAs throw on exotic strings — ignore */
    }
  }
  return undefined;
}
